import type {
  MpVisualImportPage,
  MpVisualImportRow,
  MpVisualSource,
  SiteCardCampaign,
  SiteCardCampaignStatus,
  SiteCategoryFields,
  SiteCategoryPreview,
  SiteFacetOption,
  SiteProduct,
  SiteProductCard,
  SiteProductFilters,
  SiteProductStats,
  SiteProductStatus,
  SiteRecordPreview,
  SiteConnectionResult,
  SiteFullSchedule,
  SiteRun,
  SiteRunDetails,
  SiteSettings,
  SiteSettingsDraft,
} from './types'

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

const text = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value).trim()

export function normalizeSiteCategoryPreview(value: unknown): SiteCategoryPreview {
  const source = asRecord(value)
  return {
    article: text(source.article),
    theme: text(source.theme),
    brand: text(source.brand),
    categoryName: text(source.categoryName ?? source.category_name),
    title: text(source.title),
  }
}

const bool = (value: unknown): boolean => value === true || value === 1 || value === '1'

const numberOr = (value: unknown, fallback: number): number =>
  Number.isFinite(Number(value)) ? Number(value) : fallback

const nullableText = (value: unknown): string | null => {
  const result = text(value)
  return result ? result : null
}

export const EMPTY_SITE_SETTINGS: SiteSettings = {
  siteUrl: '',
  apiPath: '/wp-json/printort/v1',
  username: '',
  hasToken: false,
  batchSize: 500,
  exchangeEnabled: false,
  autoIncremental: false,
  fullSchedule: 'manual',
  lastRunAt: null,
  lastRunStatus: null,
  configured: false,
}

export function normalizeSiteSettings(value: unknown): SiteSettings {
  const source = asRecord(value)
  const siteUrl = text(source.siteUrl ?? source.site_url)
  const hasToken = bool(source.hasToken ?? source.has_token)
  return {
    siteUrl,
    apiPath: text(source.apiPath ?? source.api_path) || '/wp-json/printort/v1',
    username: text(source.username),
    hasToken,
    batchSize: numberOr(source.batchSize ?? source.batch_size, 500),
    exchangeEnabled: bool(source.exchangeEnabled ?? source.exchange_enabled),
    autoIncremental: bool(source.autoIncremental ?? source.auto_incremental),
    fullSchedule: (source.fullSchedule ?? source.full_schedule) === 'daily' ? 'daily' : 'manual',
    lastRunAt: nullableText(source.lastRunAt ?? source.last_run_at),
    lastRunStatus: nullableText(source.lastRunStatus ?? source.last_run_status),
    // Признак считается на сервере, но пересчитываем и здесь: экран не должен
    // предлагать включить обмен, если ответ старого формата его не принёс.
    configured: bool(source.configured) || (Boolean(siteUrl) && hasToken),
  }
}

/** Настройки → черновик формы. Токен не переносится: его сюда никогда не отдают. */
export function siteSettingsDraft(settings: SiteSettings): SiteSettingsDraft {
  return {
    siteUrl: settings.siteUrl,
    apiPath: settings.apiPath,
    username: settings.username,
    batchSize: settings.batchSize,
    exchangeEnabled: settings.exchangeEnabled,
    autoIncremental: settings.autoIncremental,
    fullSchedule: settings.fullSchedule,
  }
}

export function normalizeSiteConnectionResult(value: unknown): SiteConnectionResult {
  const source = asRecord(value)
  return {
    ok: bool(source.ok),
    message: text(source.message) || 'Ответ без пояснения',
    plugin: nullableText(source.plugin),
    version: nullableText(source.version),
    schemaVersion: Number.isFinite(Number(source.schemaVersion))
      ? Number(source.schemaVersion)
      : null,
    products: Number.isFinite(Number(source.products)) ? Number(source.products) : null,
  }
}

function normalizeRun(value: unknown): SiteRun {
  const source = asRecord(value)
  return {
    id: text(source.id),
    mode: text(source.mode),
    status: text(source.status),
    dryRun: bool(source.dryRun),
    since: nullableText(source.since),
    totalCount: numberOr(source.totalCount, 0),
    appliedCount: numberOr(source.appliedCount, 0),
    unchangedCount: numberOr(source.unchangedCount, 0),
    skippedCount: numberOr(source.skippedCount, 0),
    failedCount: numberOr(source.failedCount, 0),
    batchTotal: numberOr(source.batchTotal, 0),
    checkpointSeq:
      source.checkpointSeq === null || source.checkpointSeq === undefined
        ? null
        : numberOr(source.checkpointSeq, 0),
    errorSummary: nullableText(source.errorSummary),
    startedAt: nullableText(source.startedAt),
    completedAt: nullableText(source.completedAt),
    createdAt: nullableText(source.createdAt),
  }
}

export function normalizeSiteRuns(value: unknown): SiteRun[] {
  const source = asRecord(value)
  const list = Array.isArray(source.runs) ? source.runs : Array.isArray(value) ? value : []
  return list.map(normalizeRun)
}

export function normalizeSiteRunDetails(value: unknown): SiteRunDetails {
  const source = asRecord(value)
  const items = Array.isArray(source.items) ? source.items : []
  return {
    ...normalizeRun(source),
    itemsTotal: numberOr(source.itemsTotal, items.length),
    items: items.map((item) => {
      const row = asRecord(item)
      return {
        article: text(row.article),
        batchSeq:
          row.batchSeq === null || row.batchSeq === undefined ? null : numberOr(row.batchSeq, 0),
        decision: text(row.decision),
        reason: nullableText(row.reason),
        postId: row.postId === null || row.postId === undefined ? null : numberOr(row.postId, 0),
        error: nullableText(row.error),
        createdAt: nullableText(row.createdAt),
      }
    }),
  }
}

export const EMPTY_SITE_PRODUCT_FILTERS: SiteProductFilters = {
  search: '',
  articles: [],
  categories: [],
  designCategories: [],
  designTypes: [],
  visual: '',
  hasPrice: '',
  seoSource: '',
  priceFrom: '',
  priceTo: '',
  dateFrom: '',
  dateTo: '',
}

export function countSiteProductFilters(filters: SiteProductFilters): number {
  return (
    (filters.search.trim() ? 1 : 0) +
    (filters.articles.length ? 1 : 0) +
    filters.categories.length +
    filters.designCategories.length +
    filters.designTypes.length +
    (filters.visual ? 1 : 0) +
    (filters.hasPrice ? 1 : 0) +
    (filters.seoSource ? 1 : 0) +
    (filters.priceFrom || filters.priceTo ? 1 : 0) +
    (filters.dateFrom || filters.dateTo ? 1 : 0)
  )
}

/**
 * Сколько фильтров живёт в раскрывающейся панели «Все фильтры».
 *
 * Считается отдельно от общего числа: бейдж на кнопке должен обещать ровно то,
 * что человек увидит, раскрыв панель, — иначе он ищет в ней носитель, который
 * выбран дропдауном тулбара.
 */
export function countSitePanelFilters(filters: SiteProductFilters): number {
  return (
    (filters.visual ? 1 : 0) +
    (filters.hasPrice ? 1 : 0) +
    (filters.priceFrom || filters.priceTo ? 1 : 0)
  )
}

const facet = (value: unknown): SiteFacetOption[] =>
  (Array.isArray(value) ? value : []).map((item) => {
    const row = asRecord(item)
    return { value: text(row.value), label: text(row.label) || text(row.value) }
  })

export function normalizeSiteProductStats(value: unknown): SiteProductStats {
  const source = asRecord(value)
  const facets = asRecord(source.facets)
  return {
    total: numberOr(source.total, 0),
    draft: numberOr(source.draft, 0),
    sent: numberOr(source.sent, 0),
    published: numberOr(source.published, 0),
    notReady: numberOr(source.notReady, 0),
    errors: numberOr(source.errors, 0),
    facets: {
      categories: facet(facets.categories),
      designCategories: facet(facets.designCategories),
      designTypes: facet(facets.designTypes),
    },
  }
}

export function normalizeSiteProduct(value: unknown): SiteProduct {
  const row = asRecord(value)
  const price = row.price === null || row.price === undefined ? null : numberOr(row.price, 0)
  const seoSource = text(row.seoSource)
  const rawStatus = text(row.status)
  const status: SiteProductStatus = [
    'draft',
    'creating',
    'created',
    'price_set',
    'stock_set',
    'selling',
    'error',
    'not_ready',
  ].includes(rawStatus)
    ? (rawStatus as SiteProductStatus)
    : 'not_ready'
  return {
    article: text(row.article),
    designCode: nullableText(row.designCode),
    pimTitle: nullableText(row.pimTitle),
    siteTitle: nullableText(row.siteTitle),
    carrier: nullableText(row.carrier),
    carrierPrefix: nullableText(row.carrierPrefix),
    category: nullableText(row.category),
    theme: nullableText(row.theme),
    designType: nullableText(row.designType),
    price,
    hasVisual: bool(row.hasVisual),
    visualUrl: nullableText(row.visualUrl),
    visualPath: nullableText(row.visualPath),
    seoSource: seoSource === 'site' || seoSource === 'inherited_wb' ? seoSource : null,
    status,
    cardId: row.cardId === null || row.cardId === undefined || row.cardId === ''
      ? null
      : (Number.isFinite(Number(row.cardId)) && Number(row.cardId) > 0 ? Number(row.cardId) : null),
    externalId: nullableText(row.externalId),
    postId: row.postId === null || row.postId === undefined ? null : numberOr(row.postId, 0),
    reason: nullableText(row.reason),
    lastRunAt: nullableText(row.lastRunAt),
    updatedAt: nullableText(row.updatedAt),
  }
}

export function normalizeSiteProductsPage(value: unknown): {
  rows: SiteProduct[]
  total: number
  totalIsApproximate: boolean
} {
  const source = asRecord(value)
  const rows = Array.isArray(source.rows) ? source.rows : []
  return {
    rows: rows.map(normalizeSiteProduct),
    total: numberOr(source.total, rows.length),
    totalIsApproximate: bool(source.totalIsApproximate),
  }
}

export function normalizeSiteProductCard(value: unknown): SiteProductCard {
  const source = asRecord(value)
  const pairs = (raw: unknown) =>
    (Array.isArray(raw) ? raw : []).map((item) => {
      const row = asRecord(item)
      return { article: text(row.article), reason: text(row.reason) }
    })
  return {
    product: normalizeSiteProduct(source.product),
    history: (Array.isArray(source.history) ? source.history : []).map((item) => {
      const row = asRecord(item)
      return {
        runId: text(row.runId),
        mode: text(row.mode),
        dryRun: bool(row.dryRun),
        decision: text(row.decision),
        reason: nullableText(row.reason),
        postId: row.postId === null || row.postId === undefined ? null : numberOr(row.postId, 0),
        error: nullableText(row.error),
        createdAt: nullableText(row.createdAt),
      }
    }),
    record: source.record ? asRecord(source.record) : null,
    skipped: pairs(source.skipped),
    gaps: pairs(source.gaps),
  }
}

export function normalizeSiteRecordPreview(value: unknown): SiteRecordPreview {
  const source = asRecord(value)
  const pairs = (raw: unknown) =>
    (Array.isArray(raw) ? raw : []).map((item) => {
      const row = asRecord(item)
      return { article: text(row.article), reason: text(row.reason) }
    })
  return {
    records: (Array.isArray(source.records) ? source.records : []).map((item) => asRecord(item)),
    skipped: pairs(source.skipped),
    gaps: pairs(source.gaps),
  }
}

/**
 * Человекочитаемая причина отказа НАШЕГО API.
 *
 * Главное правило то же, что у вкладки 1С: код ответа принадлежит гейтвею PIM,
 * а не сайту. 401 отсюда — истёкшая сессия человека, 403 — нехватка права
 * `Site`. Отказы самого плагина сайта приходят полем `message` в результате
 * проверки связи и переводятся на бэкенде, у самого транспорта.
 */
export function humanizeSiteError(error: unknown): string {
  const source = asRecord(error)
  const response = asRecord(source.response)
  const status = Number(response.status ?? source.status)
  if (status === 401) return 'Сессия истекла. Войдите заново'
  if (status === 403) return 'У вашей роли нет права на обмен с сайтом'
  if (status === 404) return 'Маршрут обмена не найден. Похоже, гейтвей ещё не обновлён'
  const data = asRecord(response.data)
  const payload = asRecord(data.error)
  const message = text(payload.message) || text(data.message) || text(source.message)
  return message || 'Не удалось выполнить операцию обмена с сайтом'
}

/**
 * Шаблон наименования сайта → строка.
 *
 * Подстановок четыре, а не три как у 1С: витрине нужен бренд — по нему человек
 * узнаёт сюжет («Свинка Пеппа») быстрее, чем по тематике.
 *
 * Пустая подстановка схлопывается вместе с лишними пробелами: макет без бренда
 * не должен давать наименование с провалом посередине.
 */
export function interpolateSiteName(
  template: string,
  values: {
    category?: string | null
    theme?: string | null
    article?: string | null
    brand?: string | null
  },
): string {
  const fallback = '{category} {theme}'
  return (template.trim() || fallback)
    .replaceAll('{category}', values.category ?? '')
    .replaceAll('{theme}', values.theme ?? '')
    .replaceAll('{article}', values.article ?? '')
    .replaceAll('{brand}', values.brand ?? '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Слаг витрины: латиница, цифры и дефис. Проверяем, а не чиним молча. */
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/**
 * Замечания вкладки «Сайт».
 *
 * Проверяем только при включённой выгрузке. Выключенная категория с пустыми
 * полями — это не «не заполнено», а «на сайт не идёт», и требовать от неё слаг
 * значило бы запирать сохранение всем 33 категориям сразу.
 */
export function siteCategoryIssues(
  form: SiteCategoryFields,
): Array<{ field: string; message: string }> {
  const issues: Array<{ field: string; message: string }> = []
  if (Number(form.site_enabled ?? 0) !== 1) return issues

  if (!text(form.site_name)) {
    issues.push({ field: 'siteName', message: 'Не задано название носителя на витрине' })
  }

  const slug = text(form.site_slug)
  if (!slug) {
    issues.push({ field: 'siteSlug', message: 'Не задан слаг страницы носителя' })
  } else if (!SLUG_PATTERN.test(slug)) {
    issues.push({
      field: 'siteSlug',
      message: 'Слаг может содержать только строчные латинские буквы, цифры и дефис',
    })
  }

  const price = form.site_price
  if (price !== null && price !== undefined && price !== '' && Number(price) < 0) {
    issues.push({ field: 'sitePrice', message: 'Цена сайта не может быть отрицательной' })
  }

  return issues
}

/**
 * Кампания заведения карточек сайта из ответа бэкенда.
 *
 * Счётчики нормализуются к числам: сумма пропусков идёт в отчёт прогона, а
 * «—» вместо числа читалось бы как «пропусков не было».
 */
export function normalizeSiteCardCampaign(value: unknown): SiteCardCampaign | null {
  const source = asRecord(value)
  const id = text(source.id)
  if (!id) return null
  const skipped = asRecord(source.skipped)
  const filters = asRecord(source.filters)
  return {
    id,
    // Кампании, заведённые до появления режима визуалов, режима не знают —
    // читаем их как «карточки», иначе плашка старой волны показала бы счётчики
    // не той работы.
    mode: text(source.mode) === 'visuals' ? 'visuals' : 'cards',
    categories: Array.isArray(filters.categories)
      ? filters.categories.map((item) => text(item)).filter(Boolean)
      : [],
    view: (text(source.view) || 'not_ready') as SiteCardCampaign['view'],
    status: (text(source.status) || 'queued') as SiteCardCampaignStatus,
    totalCount: numberOr(source.totalCount ?? source.total_count, 0),
    processedCount: numberOr(source.processedCount ?? source.processed_count, 0),
    cardsCreated: numberOr(source.cardsCreated ?? source.cards_created, 0),
    designsLaunched: numberOr(source.designsLaunched ?? source.designs_launched, 0),
    withoutPhoto: numberOr(source.withoutPhoto ?? source.without_photo, 0),
    withoutSeo: numberOr(source.withoutSeo ?? source.without_seo, 0),
    skipped: {
      alreadyHasCard: numberOr(skipped.alreadyHasCard ?? skipped.already_has_card, 0),
      noCategory: numberOr(skipped.noCategory ?? skipped.no_category, 0),
      noDesign: numberOr(skipped.noDesign ?? skipped.no_design, 0),
      notFound: numberOr(skipped.notFound ?? skipped.not_found, 0),
    },
    failedCount: numberOr(source.failedCount ?? source.failed_count, 0),
    failedDesigns: numberOr(source.failedDesigns ?? source.failed_designs, 0),
    checkpointArticle: nullableText(source.checkpointArticle ?? source.checkpoint_article),
    checkpointDesign: nullableText(source.checkpointDesign ?? source.checkpoint_design),
    jobId: nullableText(source.jobId ?? source.job_id),
    errorSummary: nullableText(source.errorSummary ?? source.error_summary),
    startedAt: nullableText(source.startedAt ?? source.started_at),
    completedAt: nullableText(source.completedAt ?? source.completed_at),
    createdAt: nullableText(source.createdAt ?? source.created_at),
  }
}

export function normalizeSiteCardCampaigns(value: unknown): {
  campaigns: SiteCardCampaign[]
  active: SiteCardCampaign | null
  last: SiteCardCampaign | null
} {
  const source = asRecord(value)
  const list = Array.isArray(source.campaigns) ? source.campaigns : []
  return {
    campaigns: list
      .map((item) => normalizeSiteCardCampaign(item))
      .filter((item): item is SiteCardCampaign => item !== null),
    active: normalizeSiteCardCampaign(source.active),
    // Последняя кампания в любом состоянии: короткий прогон завершается за доли
    // секунды, и по одной только активной экран показал бы пустоту.
    last: normalizeSiteCardCampaign(source.last),
  }
}

/**
 * Строка предпросмотра переноса визуалов.
 *
 * Кадры источника оставляем как есть — это адреса витрины площадки, их
 * показывает браузер человека. В карточку они не попадут: перенос скачивает
 * файлы на сервер, а сайт подключает картинку только к локальному файлу.
 */
function normalizeMpVisualSource(value: unknown): MpVisualSource {
  const source = asRecord(value)
  const photos = (Array.isArray(source.photos) ? source.photos : [])
    .map((item) => text(item))
    .filter(Boolean)
  return {
    key: text(source.key),
    marketplace: text(source.marketplace) === 'ozon' ? 'ozon' : 'wb',
    sellerId: numberOr(source.sellerId, 0),
    cabinet: text(source.cabinet),
    sellerName: text(source.sellerName),
    externalId: text(source.externalId),
    title: text(source.title),
    photoCount: numberOr(source.photoCount, photos.length),
    photos,
    updatedAt: nullableText(source.updatedAt),
  }
}

export function normalizeMpVisualImportRow(value: unknown): MpVisualImportRow {
  const source = asRecord(value)
  const card = asRecord(source.siteCard)
  const sources = (Array.isArray(source.sources) ? source.sources : []).map(normalizeMpVisualSource)
  return {
    article: text(source.article),
    title: text(source.title),
    designCode: nullableText(source.designCode),
    external: bool(source.external),
    siteCard: {
      exists: bool(card.exists),
      status: nullableText(card.status),
      visualCount: numberOr(card.visualCount, 0),
      visualUrl: nullableText(card.visualUrl),
    },
    sources,
    defaultSourceKey: nullableText(source.defaultSourceKey),
  }
}

export function normalizeMpVisualImportPage(value: unknown): MpVisualImportPage {
  const source = asRecord(value)
  const rows = Array.isArray(source.rows) ? source.rows : []
  return {
    rows: rows.map(normalizeMpVisualImportRow),
    nextAfter: nullableText(source.nextAfter),
    total: numberOr(source.total, rows.length),
  }
}
